import { parseApiDate } from '@/lib/dates';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp } from 'lucide-react';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/groups/StatusPill';
import { formatSeats, type GroupListMeta, type GroupSort, type GroupSummary } from '@/types/groups';

const SEARCH_DEBOUNCE_MS = 300;

function formatDate(iso: string): string {
  return parseApiDate(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function GroupsPage() {
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [meta, setMeta] = useState<GroupListMeta | null>(null);
  const [search, setSearch] = useState('');
  // Only the search term is debounced. Sorting and paging are deliberate
  // clicks, and making those wait 300ms for no reason just feels broken.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<GroupSort>('newest');
  const [page, setPage] = useState(1);
  /**
   * The filter combination currently on screen, and the one the last settled
   * response was for. Deriving isLoading from the two means the flag never has
   * to be switched on from inside the effect.
   */
  const requestKey = JSON.stringify([debouncedSearch, sort, page]);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const isLoading = loadedKey !== requestKey;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    // `active` is what makes a superseded response harmless: the cleanup runs
    // before the next fetch starts and on unmount, so a slow early response can
    // no longer repaint the table with results the user has already moved past.
    let active = true;

    void (async () => {
      try {
        const params = new URLSearchParams({ sort, page: String(page) });
        if (debouncedSearch) {
          params.set('search', debouncedSearch);
        }
        const response = await api.get(`/groups?${params.toString()}`);
        if (!active) return;
        setGroups(response.data.data);
        setMeta(response.data.meta);
      } catch (error) {
        if (!active) return;
        // Without this the table just stays empty, which reads identically to a
        // deployment that genuinely has no groups.
        setGroups([]);
        setMeta(null);
        toast.error(getApiErrorMessage(error), { id: 'groups-directory-error' });
      }
      if (active) setLoadedKey(requestKey);
    })();

    return () => {
      active = false;
    };
  }, [debouncedSearch, sort, page, requestKey]);

  const toggleMemberSort = () => {
    setPage(1);
    // Fewest-first is the useful default here: it is the order that surfaces
    // groups that were created and then never grew.
    setSort((current) => (current === 'members_asc' ? 'members_desc' : 'members_asc'));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold">Groups</h1>
        <p className="text-muted-foreground">
          Every group on the platform, with how many members have actually joined.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Input
              className="max-w-sm"
              placeholder="Search by name or code"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            {meta && (
              <p className="text-sm text-muted-foreground">
                {meta.total} {meta.total === 1 ? 'group' : 'groups'}
              </p>
            )}
          </div>

          {groups.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {isLoading ? 'Loading groups.' : 'No groups match this search.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Group</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8 font-medium"
                        onClick={toggleMemberSort}
                      >
                        Members
                        {sort === 'members_asc' && <ArrowUp className="ml-1 h-3 w-3" />}
                        {sort === 'members_desc' && <ArrowDown className="ml-1 h-3 w-3" />}
                      </Button>
                    </TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groups.map((group) => (
                    <TableRow key={group.uuid}>
                      <TableCell className="font-medium">
                        <Link className="hover:underline" to={`/groups/${group.uuid}`}>
                          {group.name}
                        </Link>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{group.code}</TableCell>
                      <TableCell className="whitespace-nowrap">{group.countryName ?? 'Not set'}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {formatSeats(group.memberCount, group.memberLimit)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{group.tierName ?? 'None'}</TableCell>
                      <TableCell>
                        <StatusPill status={group.subscriptionStatus} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(group.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {meta && meta.lastPage > 1 && (
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {meta.currentPage} of {meta.lastPage}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= meta.lastPage}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
